import React from 'react';
import { CheckCircle2, Circle, Package, Truck, Home, ExternalLink } from 'lucide-react';

/**
 * Visual order tracking timeline with public carrier link.
 * Used in customer profile, public tracking page and admin detail.
 *
 * Props:
 *   - order: { order_status, shipping_carrier?, tracking_number?, tracking_url? }
 *   - compact: bool — render a smaller variant for list views
 */
const STEPS = [
  { key: 'pending', label: 'Recibido', icon: Package },
  { key: 'processing', label: 'Preparando', icon: Package },
  { key: 'shipped', label: 'Enviado', icon: Truck },
  { key: 'delivered', label: 'Entregado', icon: Home },
];

const STATUS_ORDER = ['pending', 'processing', 'shipped', 'delivered'];

export default function OrderTrackingTimeline({ order, compact = false }) {
  if (!order) return null;

  const status = order.order_status || 'pending';
  const isCancelled = status === 'cancelled';
  const activeIdx = STATUS_ORDER.indexOf(status);

  const carrier = order.shipping_carrier;
  const trackingNumber = order.tracking_number;
  const trackingUrl = order.tracking_url;

  return (
    <div className="bg-white border border-gray-200 p-4 sm:p-6 rounded" data-testid="order-tracking-timeline">
      {isCancelled ? (
        <div className="bg-red-50 border border-red-200 p-4 rounded text-center">
          <p className="font-bold text-red-800">Pedido cancelado</p>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            const done = idx <= activeIdx;
            const current = idx === activeIdx;
            return (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center flex-shrink-0 min-w-0">
                  <div
                    className={`w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-colors ${
                      done ? 'bg-black text-white' : 'bg-gray-100 text-gray-400'
                    } ${current ? 'ring-4 ring-black/10' : ''}`}
                    data-testid={`tracking-step-${step.key}`}
                  >
                    {done && !current ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  {!compact && (
                    <p className={`text-xs mt-2 text-center ${done ? 'font-bold text-black' : 'text-gray-400'}`}>
                      {step.label}
                    </p>
                  )}
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-1 sm:mx-2 ${idx < activeIdx ? 'bg-black' : 'bg-gray-200'}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {(carrier || trackingNumber || trackingUrl) && !isCancelled && (
        <div className="mt-5 pt-5 border-t border-gray-100">
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            {carrier && (
              <div className="text-sm">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Transportista</p>
                <p className="font-medium" data-testid="tracking-carrier">{carrier}</p>
              </div>
            )}
            {trackingNumber && (
              <div className="text-sm">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Nº de seguimiento</p>
                <p className="font-mono font-medium select-all" data-testid="tracking-number">{trackingNumber}</p>
              </div>
            )}
          </div>
          {trackingUrl && (
            <a
              href={trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-black text-white px-4 py-2.5 rounded font-medium hover:bg-gray-800 transition-colors w-full sm:w-auto"
              data-testid="tracking-public-link"
            >
              <Circle className="w-4 h-4" />
              Ver seguimiento en {carrier || 'el transportista'}
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}
